/**
 * 菜单模块 - 常量定义
 * 
 * @file-purpose 定义菜单模块的常量和映射关系
 * @backend-file backend/app/Models/Menu.php
 * 
 * @attention
 * 1. 常量名使用 UPPER_SNAKE_CASE
 * 2. LABELS 对象的键必须与枚举值一致
 * 3. 权限常量必须与 PermissionSeeder.php 一致
 * 4. MENU_TYPE_LABELS 和 MENU_PLATFORM_LABELS 从 constants/status.ts 重新导出
 * 
 * @update-timing
 * - 后端 Model 新增常量时
 * - 后端修改中文标签时
 * - 后端 PermissionSeeder 新增菜单权限时
 * 
 * @validation
 * 1. 对照 backend/app/Models/Menu.php 的静态属性
 * 2. 对照 backend/database/seeders/PermissionSeeder.php
 * 3. 确保所有枚举值都有对应的中文标签
 * 
 * @author YSFL Team
 * @last-updated 2025-10-18
 */

import { MenuType, MenuPlatform, MenuStatus, BadgeType, BadgeVariant } from './enums'
import { 
  MENU_TYPE_LABELS as _MENU_TYPE_LABELS, 
  MENU_PLATFORM_LABELS as _MENU_PLATFORM_LABELS 
} from '../../constants/status'

/**
 * 菜单类型标签映射（从 constants/status.ts 重新导出）
 * 
 * @description 菜单类型的中文显示文本
 */
export const MENU_TYPE_LABELS = _MENU_TYPE_LABELS as Record<MenuType, string>

/**
 * 菜单平台标签映射（从 constants/status.ts 重新导出）
 * 
 * @description 菜单平台的中文显示文本
 */
export const MENU_PLATFORM_LABELS = _MENU_PLATFORM_LABELS as Record<MenuPlatform, string>

/**
 * 菜单状态标签映射
 * 
 * @description 菜单状态的中文显示文本
 */
export const MENU_STATUS_LABELS: Record<MenuStatus, string> = {
  [MenuStatus.DISABLED]: '禁用',
  [MenuStatus.ENABLED]: '启用',
}

/**
 * 徽标类型标签映射
 * 
 * @description 徽标类型的中文显示文本
 */
export const BADGE_TYPE_LABELS: Record<BadgeType, string> = {
  [BadgeType.DOT]: '圆点',
  [BadgeType.NORMAL]: '数字/文本',
}

/**
 * 徽标变体标签映射
 * 
 * @description 徽标变体的中文显示文本
 */
export const BADGE_VARIANT_LABELS: Record<BadgeVariant, string> = {
  [BadgeVariant.DEFAULT]: '默认',
  [BadgeVariant.DESTRUCTIVE]: '危险',
  [BadgeVariant.SUCCESS]: '成功',
  [BadgeVariant.WARNING]: '警告',
}

/**
 * ⚠️ 菜单管理权限常量已迁移至统一权限管理
 * 
 * 请从 @bayes-mes/shared 导入标准权限常量：
 * - PERM_SYSTEM_MENU_VIEW
 * - PERM_SYSTEM_MENU_CREATE
 * - PERM_SYSTEM_MENU_UPDATE
 * - PERM_SYSTEM_MENU_DELETE
 * 
 * @deprecated 请使用 constants/api-permissions.ts 中的权限常量
 * @see bayes-mes-shared/constants/api-permissions.ts
 */

/**
 * 默认分页配置
 */
export const DEFAULT_MENU_PAGE_SIZE = 20

/**
 * 默认排序字段
 */
export const DEFAULT_MENU_SORT_BY = 'sort_order'

/**
 * 默认排序方向
 */
export const DEFAULT_MENU_SORT_ORDER = 'asc'

/**
 * 菜单名称最大长度
 */
export const MENU_NAME_MAX_LENGTH = 50

/**
 * 菜单路径最大长度
 */
export const MENU_PATH_MAX_LENGTH = 200

/**
 * 菜单标题最大长度
 */
export const MENU_TITLE_MAX_LENGTH = 50

/**
 * 组件路径最大长度
 */
export const MENU_COMPONENT_MAX_LENGTH = 200

/**
 * 备注最大长度
 */
export const MENU_REMARK_MAX_LENGTH = 500

/**
 * 顶级菜单的父ID
 */
export const ROOT_MENU_PID = 0

/**
 * 菜单类型选项（用于表单下拉）
 */
export const MENU_TYPE_OPTIONS = [
  { label: MENU_TYPE_LABELS[MenuType.CATALOG], value: MenuType.CATALOG },
  { label: MENU_TYPE_LABELS[MenuType.MENU], value: MenuType.MENU },
  { label: MENU_TYPE_LABELS[MenuType.BUTTON], value: MenuType.BUTTON },
  { label: MENU_TYPE_LABELS[MenuType.EMBEDDED], value: MenuType.EMBEDDED },
  { label: MENU_TYPE_LABELS[MenuType.LINK], value: MenuType.LINK },
]

/**
 * 菜单平台选项（用于表单下拉）
 */
export const MENU_PLATFORM_OPTIONS = [
  { label: MENU_PLATFORM_LABELS[MenuPlatform.WEB], value: MenuPlatform.WEB },
  { label: MENU_PLATFORM_LABELS[MenuPlatform.APP], value: MenuPlatform.APP },
]

/**
 * 菜单状态选项（用于表单下拉）
 */
export const MENU_STATUS_OPTIONS = [
  { label: MENU_STATUS_LABELS[MenuStatus.ENABLED], value: MenuStatus.ENABLED },
  { label: MENU_STATUS_LABELS[MenuStatus.DISABLED], value: MenuStatus.DISABLED },
]

